import { alert } from '@/services/alert';
import { getContactLabel } from '@/services/contact-point';
import { buildPhoneCardDetails } from '@/services/Timeline/phoneCardDetails';
import { formatPhoneNumber, isSamePhoneNumber, normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import { ActionIcon, Flex, Group, Menu, Text, Tooltip } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { IconArrowBackUp, IconFilePlus, IconLink } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { getPatientPhones } from '@/pages/PatientTimelinePage/services/timelinePatient';
import { getOrganizationPhones } from '@/pages/PartnersPage/PartnerTimeline/services/timelineOrganization';
import { MessageBoxType, SmsTimelineGroup, TimelineNote } from '../types';
import { TextMessageTimelineItem } from './TextMessageTimelineItem';
import { CARD_MENU_ICON_COLOR, CardMenu } from './CardMenu';
import { DateTimeTooltip } from './DateTimeTooltip';
import { SmsThreadAvatar } from './SmsThreadAvatar';

interface SmsTimelineHeaderProps {
  group: SmsTimelineGroup;
  /** Card-level sticky note. Attaches to the newest text in the thread — a
   * note about "this conversation" is a note about where it left off. */
  onAddNote: () => void;
  isNoteInputOpen: boolean;
  onCopyLink: () => void;
}

export function SmsTimelineHeader({ group, onAddNote, isNoteInputOpen, onCopyLink }: SmsTimelineHeaderProps) {
  const { patientContext, partnerContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const organization = partnerContext?.organization;
  const isPartnerContext = !!partnerContext;

  const { setSelectedPhone, setMessageBoxVariant, setShowMessageInput, setAutofocusMessageBox } = messageBoxContext;

  const patientPhones = useMemo(() => (patient ? getPatientPhones(patient) : []), [patient]);
  const organizationPhones = useMemo(() => getOrganizationPhones(organization), [organization]);

  const phones = isPartnerContext ? organizationPhones : patientPhones;

  const groupPhoneNumber = group.items[0]?.patientNumber;
  const phoneLabel = getContactLabel(
    phones.find((phone) => normalizePhoneNumber(phone.value ?? '') === groupPhoneNumber)
  );
  const phone = { number: groupPhoneNumber, label: phoneLabel };

  const handleReplySms = (phoneNumber?: string) => {
    setMessageBoxVariant(MessageBoxType.SMS);

    const selectedPhoneNumber = phoneNumber ?? phone.number;

    const selectedPhoneContact = phones.find((phone) => isSamePhoneNumber(phone.value, selectedPhoneNumber));
    if (!selectedPhoneContact) {
      alert.error({
        title: `${isPartnerContext ? 'Partner' : 'Patient'} phone ${formatPhoneNumber(selectedPhoneNumber)} not found`,
        message: `This phone number is not associated with the ${isPartnerContext ? 'partner' : 'patient'}`,
      });
      return;
    }
    setSelectedPhone(selectedPhoneContact);
    setShowMessageInput(true);
    setAutofocusMessageBox(true);
  };

  // Every Everself line the thread has used, newest first — a thread can span
  // several, and which one a reply leaves on decides where it lands.
  const details = useMemo(
    () =>
      buildPhoneCardDetails({
        patientNumber: phone.number,
        orbitNumbers: group.items.map((item) => item.orbitNumber),
        patientLabel: phone.label,
      }),
    [phone.number, phone.label, group.items]
  );

  return (
    <Group justify="space-between" align="center" wrap="nowrap">
      <Flex gap="xs" align="center" style={{ minWidth: 0 }}>
        <Text size="md" fw={700} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          SMS
        </Text>
      </Flex>

      {/* Canon header cluster: actions -> overflow -> avatar -> time. */}
      <Group gap="xs" align="center" style={{ flexShrink: 0 }}>
        <Tooltip label="Reply">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Reply"
            onClick={() => handleReplySms(phone.number)}
          >
            <IconArrowBackUp size={18} />
          </ActionIcon>
        </Tooltip>
        <CardMenu aria-label="More text actions" details={details}>
          <Menu.Item
            leftSection={<IconFilePlus size={18} color={CARD_MENU_ICON_COLOR} />}
            onClick={onAddNote}
            disabled={isNoteInputOpen}
          >
            Add sticky note
          </Menu.Item>
          <Menu.Item leftSection={<IconLink size={18} color={CARD_MENU_ICON_COLOR} />} onClick={onCopyLink}>
            Copy link
          </Menu.Item>
        </CardMenu>
        <SmsThreadAvatar group={group} />
        <DateTimeTooltip date={group.timestamp} />
      </Group>
    </Group>
  );
}

interface SmsTimelineItemProps {
  group: SmsTimelineGroup;
  notes?: TimelineNote[];
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
}

export function SmsTimelineItem({
  group,
  notes = [],
  highlightRef,
  copyDirectLink,
  hasActiveFilters,
  handleNavigateToItem,
  searchTerm,
}: SmsTimelineItemProps) {
  // Card actions act on the newest text. Groups are newest-first
  // (`sortByTimestampDesc`), so that is items[0] — NOT the last element.
  const latestMessage = group.items[0];

  return (
    <TextMessageTimelineItem
      group={group}
      notes={notes}
      highlightRef={highlightRef}
      copyDirectLink={copyDirectLink}
      hasActiveFilters={hasActiveFilters}
      handleNavigateToItem={handleNavigateToItem}
      renderHeader={({ onAddNote, noteInputTarget }) => (
        <SmsTimelineHeader
          group={group}
          onAddNote={() => latestMessage && onAddNote(latestMessage.reference)}
          isNoteInputOpen={Boolean(latestMessage) && noteInputTarget === getReferenceString(latestMessage.reference)}
          onCopyLink={() => {
            if (latestMessage) {
              copyDirectLink(getReferenceString(latestMessage.reference) ?? '').catch(() => undefined);
            }
          }}
        />
      )}
      searchTerm={searchTerm}
    />
  );
}
